import { ref, onMounted, onUnmounted } from 'vue';

export const AI_SEARCH_SIGN_IN_MESSAGE = 'Please sign in to use AI stone search.';
export const AI_SEARCH_FORBIDDEN_MESSAGE = 'Your account does not have access to AI stone search.';

const getCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

    return match ? decodeURIComponent(match[1]) : null;
};

// Same portal token the favorites handler and the single-slab AI tools use.
export const getApiToken = () => {
    try {
        return localStorage.getItem('api_token') || getCookie('api_token') || null;
    } catch (error) {
        return getCookie('api_token');
    }
};

export const hasCustomerAiAccess = (user) => {
    if (!user) {
        return false;
    }

    if (user.has_customer_ai_access === true || user.has_customer_ai_access === 1) {
        return true;
    }

    return Array.isArray(user.permissions) && user.permissions.includes('customer_ai_tools');
};

export function useSlabUpload(items, allItems, filtersModule) {
    const isUploading = ref(false);
    const uploadedImage = ref(null);
    const isDragging = ref(false);
    const uploadError = ref(null);
    // True only for signed-in users with customer AI access; the API enforces the same rule.
    const isUploadVisible = ref(false);
    const aiDescription = ref('');
    let accessCheck = 0;

    const apiBaseUrl = () => {
        const configuredUrl = document.querySelector('meta[name="mm-api-url"]')?.getAttribute('content')
            || (typeof window !== 'undefined' ? window.apiUrl : '')
            || '';

        return String(configuredUrl).replace(/\/$/, '');
    };

    const refreshAiAccess = async () => {
        const check = ++accessCheck;
        const token = getApiToken();

        if (!token) {
            isUploadVisible.value = false;
            return;
        }

        try {
            const response = await fetch(`${apiBaseUrl()}/api/user`, {
                cache: 'no-store',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const user = response.ok ? await response.json() : null;

            if (check === accessCheck) {
                isUploadVisible.value = hasCustomerAiAccess(user);
            }
        } catch (error) {
            if (check === accessCheck) {
                isUploadVisible.value = false;
            }
        }
    };

    const onStorage = (event) => {
        if (event.key === 'api_token') {
            void refreshAiAccess();
        }
    };

    const onAuthenticated = () => {
        void refreshAiAccess();
    };

    // Stone search only reads inventory and never writes slab images, so it is
    // not held back by the image-operation pause during inventory refreshes.
    onMounted(() => {
        void refreshAiAccess();
        window.addEventListener('favorites:authenticated', onAuthenticated);
        window.addEventListener('storage', onStorage);
    });

    onUnmounted(() => {
        window.removeEventListener('favorites:authenticated', onAuthenticated);
        window.removeEventListener('storage', onStorage);
    });

    const {
        activeFilters, search, selectedOrder,
        onlyDealerItems, onlySignatureItems, onlyStockItems, onlySales, onlySingles, singlesExact, onlyNewArrivals,
        updateFilters
    } = filtersModule;

    const processImageUpload = async (file, description = '') => {
        const resolvedDescription = String(description || aiDescription.value || '').trim();

        if (!file && !resolvedDescription) {
            uploadError.value = 'Please upload an image or enter a description.';
            return;
        }

        const token = getApiToken();

        if (!token) {
            isUploadVisible.value = false;
            uploadError.value = AI_SEARCH_SIGN_IN_MESSAGE;
            return;
        }

        // Create preview when an image is provided
        if (file) {
            if (uploadedImage.value) {
                URL.revokeObjectURL(uploadedImage.value);
            }

            uploadedImage.value = URL.createObjectURL(file);
        }

        isUploading.value = true;
        uploadError.value = null;

        const formData = new FormData();
        if (file) {
            formData.append('image', file);
        }
        if (resolvedDescription) {
            formData.append('description', resolvedDescription);
        }

        try {
            const locId = document.querySelector('meta[name="mm-location-id"]')?.getAttribute('content')
                || window.locationId
                || 12;
            const response = await fetch(`${apiBaseUrl()}/api/identify-stone/${locId}`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: formData
            });

            const data = await response.json().catch(() => ({}));

            if (response.status === 401 || response.status === 403) {
                isUploadVisible.value = false;
                uploadError.value = response.status === 401
                    ? AI_SEARCH_SIGN_IN_MESSAGE
                    : AI_SEARCH_FORBIDDEN_MESSAGE;
                return;
            }

            if (!response.ok) {
                throw new Error(data.message || 'Upload failed');
            }

            const baseUrl = '';
            const singleUrl = document.querySelector('meta[name="mm-single-url"]')?.getAttribute('content') || window.singleUrl || '';

            const processMatch = (item, index, type) => ({
                ...item,
                tags: item.tags ? item.tags : '',
                thumbnail: item.thumbnail || '',
                type: "Slab",
                url: item.permalink ? item.permalink : `${baseUrl}/${singleUrl ? singleUrl + '/' : ''}${item.slug}/${item.id}`,
                category: {
                    ...item.category,
                    format: [],
                    brand: []
                },
                match_type: type,
                match_order: index
            });

            const closest = (data.closest_matches || []).map((item, i) => processMatch(item, i, 'closest'));
            const extra = (data.extra_matches || []).map((item, i) => processMatch(item, i + (data.closest_matches?.length || 0), 'extra'));

            const allMatches = [...closest, ...extra];

            if (allMatches.length > 0) {
                items.value = allMatches;
                selectedOrder.value = 'relevance';

                // Clear existing filters to show results
                Object.keys(activeFilters).forEach(key => activeFilters[key] = []);
                search.value = "";

                // Reset all other filters
                onlyDealerItems.value = false;
                onlySignatureItems.value = false;
                onlyStockItems.value = false;
                onlySales.value = false;
                onlySingles.value = false;
                singlesExact.value = false;
                onlyNewArrivals.value = false;

                updateFilters();
            } else {
                uploadError.value = "No matches found.";
            }

        } catch (e) {
            console.error(e);
            uploadError.value = "An error occurred during identification.";
        } finally {
            isUploading.value = false;
        }
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        processImageUpload(file, aiDescription.value);
    };

    const handleDescriptionSearch = () => {
        processImageUpload(null, aiDescription.value);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        isDragging.value = false;

        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            processImageUpload(event.dataTransfer.files[0]);
        }
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        isDragging.value = true;
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        isDragging.value = false;
    };

    const removeUploadedImage = () => {
        if (uploadedImage.value) {
            URL.revokeObjectURL(uploadedImage.value);
        }

        uploadedImage.value = null;
        items.value = [...allItems.value]; // Restore original items
        selectedOrder.value = 'a-z'; // Reset sort
        uploadError.value = null;
        updateFilters(); // Reset filters state
    };

    return {
        isUploadVisible,
        isUploading,
        uploadedImage,
        isDragging,
        uploadError,
        aiDescription,
        refreshAiAccess,
        processImageUpload,
        handleImageUpload,
        handleDescriptionSearch,
        handleDrop,
        handleDragEnter,
        handleDragLeave,
        removeUploadedImage
    };
}
